import React, { useState, useEffect, useRef } from "react";
import "./CompetitionsPage.css";
import { useParams } from "react-router-dom";

interface PSLCompetitor {
  id: string;
  name: string;
  tag_id: string | null;
  competition_id: string;
}

type NfcTarget = "create" | { editId: string } | null;

const PSLNamesListPage: React.FC = () => {
  const { id: competitionId } = useParams<{ id: string }>();
  const [competitors, setCompetitors] = useState<PSLCompetitor[]>([]);
  const [competitionName, setCompetitionName] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(true);
  const [passwordInput, setPasswordInput] = useState("");

  // --- Create form state ---
  const [createName, setCreateName] = useState("");
  const [createCompetitorId, setCreateCompetitorId] = useState("");
  const [createTagId, setCreateTagId] = useState<string | null>(null);
  const [createNfcStatus, setCreateNfcStatus] = useState("Scan Tag");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createManualInput, setCreateManualInput] = useState(false);
  const [createManualTagValue, setCreateManualTagValue] = useState("");

  // --- Edit state ---
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editTagId, setEditTagId] = useState<string | null>(null);
  const [editNfcStatus, setEditNfcStatus] = useState("Scan Tag");
  const [editManualInput, setEditManualInput] = useState(false);
  const [editManualTagValue, setEditManualTagValue] = useState("");

  // Track which NFC flow is active: "create", { editId: string }, or null
  const nfcTargetRef = useRef<NfcTarget>(null);
  const nfcReaderRef = useRef<any>(null);
  const nfcActiveRef = useRef(false);

  const isAndroid = /Android/i.test(navigator.userAgent);
  const hasWebNfc = "NDEFReader" in window;

  // ---- Helpers ----

  const fetchCompetitors = async () => {
    try {
      const res = await fetch(
        `https://api.timebase.live/psl/get-competitors?competition_id=${competitionId}`
      );
      const data = await res.json();
      setCompetitors(data);
    } catch (err) {
      setError("Failed to load competitors.");
      console.error(err);
    }
  };

  const fetchCompetitionName = async () => {
    try {
      const res = await fetch(
        `https://api.timebase.live/psl/get-competition-name?competition_id=${competitionId}`
      );
      const name = await res.text();
      setCompetitionName(name);
    } catch (err) {
      console.error("Failed to load competition name.", err);
    }
  };

  // ---- NFC ----

  const stopNfc = () => {
    nfcActiveRef.current = false;
    nfcTargetRef.current = null;
    if (nfcReaderRef.current) {
      // Web NFC has no explicit stop; clear the reference so callbacks are ignored
      nfcReaderRef.current = null;
    }
  };

  const startNfcScan = async (target: NfcTarget) => {
    nfcTargetRef.current = target;

    if (isAndroid && hasWebNfc) {
      if (nfcActiveRef.current) {
        // Already scanning; just update target
        return;
      }
      try {
        const reader = new (window as any).NDEFReader();
        nfcReaderRef.current = reader;
        nfcActiveRef.current = true;

        await reader.scan();

        reader.onreading = (event: any) => {
          const currentTarget = nfcTargetRef.current;
          if (!currentTarget) return;

          const tagId = (event.serialNumber || "unknown")
            .replaceAll(":", "")
            .toUpperCase();

          handleTagScanned(tagId, currentTarget);
          stopNfc();
        };

        reader.onerror = () => {
          stopNfc();
          setCreateNfcStatus("Error");
          setEditNfcStatus("Error");
        };
      } catch (err) {
        console.error("NFC error:", err);
        stopNfc();
      }
    }
    // Desktop: listener registered in useEffect below
  };

  // Desktop NFC event handler
  const handleDesktopTagScanned = (e: Event) => {
    const customEvent = e as CustomEvent;
    const currentTarget = nfcTargetRef.current;
    if (!currentTarget) return;
    const tagId = customEvent.detail.tag_id;
    handleTagScanned(tagId, currentTarget);
    stopNfc();
  };

  const handleTagScanned = (tagId: string, target: NfcTarget) => {
    if (target === "create") {
      setCreateTagId(tagId);
      setCreateNfcStatus(tagId);
    } else if (target && typeof target === "object" && "editId" in target) {
      setEditTagId(tagId);
      setEditNfcStatus(tagId);
    }
  };

  // Register desktop NFC listener once
  useEffect(() => {
    if (!isAndroid || !hasWebNfc) {
      document.addEventListener("TB_tag-scanned", handleDesktopTagScanned);
      return () => {
        document.removeEventListener("TB_tag-scanned", handleDesktopTagScanned);
      };
    }
  }, []);

  // ---- Hex sanitizer ----

  const sanitizeHex = (value: string): string =>
    value.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();

  // ---- Create competitor ----

  const handleCreateScanClick = () => {
    setCreateTagId(null);
    setCreateNfcStatus("Scanning...");
    startNfcScan("create");
  };

  const handleCreateManualChange = (value: string) => {
    const clean = sanitizeHex(value);
    setCreateManualTagValue(clean);
    setCreateTagId(clean || null);
  };

  const handleCreateSubmit = async () => {
    if (!createName || !createCompetitorId || !password) {
      alert("Please fill in all fields and scan a tag.");
      return;
    }

    try {
      const res = await fetch("https://api.timebase.live/psl/map-competitor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: createName,
          competitor_id: createCompetitorId,
          tag_id: createTagId,
          competition_id: competitionId,
          password,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert("Error: " + (data.message || "Unknown error"));
        return;
      }
      // Reset form
      setCreateName("");
      setCreateCompetitorId("");
      setCreateTagId(null);
      setCreateNfcStatus("Scan Tag");
      setShowCreateForm(false);
      fetchCompetitors();
    } catch (err) {
      console.error(err);
      alert("Failed to create competitor.");
    }
  };

  // ---- Edit competitor ----

  const startEdit = (competitor: PSLCompetitor) => {
    setEditingId(competitor.id);
    setEditName(competitor.name);
    setEditTagId(competitor.tag_id);
    setEditNfcStatus(competitor.tag_id || "Scan Tag");
    setEditManualInput(false);
    setEditManualTagValue(competitor.tag_id || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    stopNfc();
    setEditNfcStatus("Scan Tag");
    setEditManualInput(false);
    setEditManualTagValue("");
  };

  const handleEditScanClick = (competitorId: string) => {
    setEditTagId(null);
    setEditNfcStatus("Scanning...");
    startNfcScan({ editId: competitorId });
  };

  const handleEditManualChange = (value: string) => {
    const clean = sanitizeHex(value);
    setEditManualTagValue(clean);
    setEditTagId(clean || null);
  };

  const handleEditSubmit = async (competitor: PSLCompetitor) => {
    if (!editName || !password) {
      alert("Please fill in all fields and scan a tag.");
      return;
    }

    try {
      const res = await fetch("https://api.timebase.live/psl/edit-competitor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editName,
          competitor_id: competitor.id,
          tag_id: editTagId,
          competition_id: competitionId,
          password,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert("Error: " + (data.message || "Unknown error"));
        return;
      }
      setEditingId(null);
      fetchCompetitors();
    } catch (err) {
      console.error(err);
      alert("Failed to update competitor.");
    }
  };

  // ---- Mount ----

  useEffect(() => {
    fetchCompetitors();
    fetchCompetitionName();
  }, [competitionId]);

  // ---- Render ----

  const filtered = competitors.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePasswordSubmit = () => {
    if (passwordInput.trim()) {
      setPassword(passwordInput.trim());
      setShowPasswordModal(false);
    }
  };

  return (
    <div style={{ padding: "1rem", maxWidth: 600, margin: "0 auto" }}>
      {showPasswordModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: "2rem",
              minWidth: 280,
              boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h3 style={{ margin: 0 }}>Admin Password</h3>
            <input
              type="password"
              placeholder="Enter password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
              autoFocus
              style={{ padding: "0.5rem", fontSize: "1rem", borderRadius: 6, border: "1px solid #ccc" }}
            />
            <button onClick={handlePasswordSubmit} disabled={!passwordInput.trim()}>
              Continue
            </button>
          </div>
        </div>
      )}
      <h2>PSL Competition: {competitionName ?? competitionId}</h2>

      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* Search */}
      <input
        type="text"
        placeholder="Search competitors"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: "1rem", display: "block", width: "100%" }}
      />

      {/* Create competitor toggle */}
      <button
        onClick={() => {
          setShowCreateForm((v) => !v);
          setCreateTagId(null);
          setCreateNfcStatus("Scan Tag");
        }}
        style={{ marginBottom: "1rem" }}
      >
        {showCreateForm ? "Cancel" : "+ Add Competitor"}
      </button>

      {showCreateForm && (
        <div
          className="name-card"
          style={{
            border: "2px dashed #888",
            padding: "1rem",
            marginBottom: "1rem",
            borderRadius: 8,
          }}
        >
          <h3 style={{ marginTop: 0 }}>New Competitor</h3>
          <div style={{ marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Competitor ID"
              value={createCompetitorId}
              onChange={(e) => setCreateCompetitorId(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
            {createManualInput ? (
              <input
                type="text"
                placeholder="Paste tag ID (hex)"
                value={createManualTagValue}
                onChange={(e) => handleCreateManualChange(e.target.value)}
                style={{ flex: 1 }}
              />
            ) : (
              <button onClick={handleCreateScanClick} style={{ flex: 1 }}>
                {createTagId ? `Tag: ${createTagId}` : createNfcStatus}
              </button>
            )}
            <button
              onClick={() => {
                setCreateManualInput((v) => !v);
                setCreateTagId(null);
                setCreateNfcStatus("Scan Tag");
                setCreateManualTagValue("");
              }}
              style={{ whiteSpace: "nowrap" }}
              title={createManualInput ? "Switch to NFC scan" : "Enter tag ID manually"}
            >
              {createManualInput ? "Use NFC" : "Manual"}
            </button>
          </div>
          <button
            onClick={handleCreateSubmit}
            disabled={!createName || !createCompetitorId || !password}
          >
            Confirm & Add
          </button>
        </div>
      )}

      {/* Competitor list */}
      <ul className="names-list">
        {filtered.map((competitor) =>
          editingId === competitor.id ? (
            <li key={competitor.id} className="name-card active-card">
              <div style={{ marginBottom: 6 }}>
                <strong>ID:</strong> {competitor.id}
              </div>
              <div style={{ marginBottom: 6 }}>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Name"
                  style={{ width: "100%" }}
                />
              </div>
              <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                {editManualInput ? (
                  <input
                    type="text"
                    placeholder="Paste tag ID (hex)"
                    value={editManualTagValue}
                    onChange={(e) => handleEditManualChange(e.target.value)}
                    style={{ flex: 1 }}
                  />
                ) : (
                  <button onClick={() => handleEditScanClick(competitor.id)} style={{ flex: 1 }}>
                    {editTagId ? `Tag: ${editTagId}` : editNfcStatus}
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditManualInput((v) => !v);
                    setEditTagId(null);
                    setEditNfcStatus("Scan Tag");
                    setEditManualTagValue("");
                  }}
                  style={{ whiteSpace: "nowrap" }}
                  title={editManualInput ? "Switch to NFC scan" : "Enter tag ID manually"}
                >
                  {editManualInput ? "Use NFC" : "Manual"}
                </button>
              </div>
              <button
                onClick={() => handleEditSubmit(competitor)}
                disabled={!editName || !password}
                style={{ marginRight: 8 }}
              >
                Save
              </button>
              <button onClick={cancelEdit}>Cancel</button>
            </li>
          ) : (
            <li
              key={competitor.id}
              className={`name-card ${competitor.tag_id ? "green-card" : ""}`}
            >
              <div>
                <strong>Name:</strong> {competitor.name}
              </div>
              <div>
                <strong>ID:</strong> {competitor.id}
              </div>
              <div>
                <strong>Tag ID:</strong> {competitor.tag_id || "None"}
              </div>
              <button
                onClick={() => startEdit(competitor)}
                style={{ marginTop: 6 }}
              >
                Edit
              </button>
            </li>
          )
        )}
      </ul>
    </div>
  );
};

export default PSLNamesListPage;
